// AladinnAcademy.jsx - Version corrigée et validée
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, Video, Filter, Grid, List, Clock, 
  Play, Loader2, X, Brain, Layers, RefreshCw, 
  Eye, ChevronDown, ChevronUp, AlertCircle, 
  BookOpen, Target, Sparkles, CheckCircle,
  Settings, BarChart3
} from 'lucide-react';

import { useVideoData } from './useVideoData';
import useAcademySynthesis from './useAcademySynthesis';
import AcademySynthesisCard from './AcademySynthesisCard';
import VideoAdminPanel from './VideoAdminPanel';

const AladinnAcademy = ({ 
  userId = null,
  objectiveId = null,
  objectiveTitle = null,
  objectiveTheme = null 
}) => {
  const navigate = useNavigate();
  
  // ================================
  // ÉTATS PRINCIPAUX - STABLES
  // ================================
  const [selectedTheme, setSelectedTheme] = useState('creativity'); // Thème par défaut stable
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [error, setError] = useState(null);

  // Objectif sélectionné - mémorisé pour éviter les re-renders
  const selectedObjectiveForVideos = useMemo(() => {
    if (objectiveId && objectiveTitle) {
      return {
        id: objectiveId,
        title: objectiveTitle,
        theme: objectiveTheme || ''
      };
    }
    return null;
  }, [objectiveId, objectiveTitle, objectiveTheme]);

  // Filtres simplifiés
  const [filters, setFilters] = useState({
    discoveryMethod: 'all',
    qualityScore: 'all',
    duration: 'all'
  });

  // ================================
  // CONFIGURATION THÈMES - STABLE
  // ================================
  const themes = useMemo(() => [
    { id: 'entrepreneurship', name: 'Entrepreneuriat', icon: '🚀', color: 'bg-blue-500', textColor: 'text-blue-800', bgLight: 'bg-blue-50', borderColor: 'border-blue-200' },
    { id: 'technology', name: 'Technologie', icon: '💻', color: 'bg-emerald-500', textColor: 'text-emerald-800', bgLight: 'bg-emerald-50', borderColor: 'border-emerald-200' },
    { id: 'finance', name: 'Finance', icon: '💰', color: 'bg-amber-500', textColor: 'text-amber-800', bgLight: 'bg-amber-50', borderColor: 'border-amber-200' },
    { id: 'leadership', name: 'Leadership', icon: '👑', color: 'bg-purple-500', textColor: 'text-purple-800', bgLight: 'bg-purple-50', borderColor: 'border-purple-200' },
    { id: 'health', name: 'Santé & Bien-être', icon: '🌱', color: 'bg-green-500', textColor: 'text-green-800', bgLight: 'bg-green-50', borderColor: 'border-green-200' },
    { id: 'creativity', name: 'Créativité', icon: '🎨', color: 'bg-pink-500', textColor: 'text-pink-800', bgLight: 'bg-pink-50', borderColor: 'border-pink-200' }
  ], []);

  // Mapping objectifs Aladinn vers thèmes vidéos - STABLE
  const ALADINN_THEME_MAPPING = useMemo(() => ({
    'Activités sportives': 'health',
    'Activité ludique et créative': 'creativity', 
    'Voyages': 'creativity',
    'Finance et Banque': 'finance',
    'Développement entrepreneurial': 'entrepreneurship',
    'Evolution de carrière': 'leadership',
    'Santé et bien-être': 'health',
    'Formation et apprentissage': 'technology',
    'Gestion immobilière': 'finance',
    'Développement personnel': 'leadership'
  }), []);

  // ================================
  // HOOK VIDÉOS (STABLE)
  // ================================
  const {
    filteredVideos, loading, error: hookError, isDiscovering, discoveryMessage,
    semanticAnalysisInfo, analysisProgress, currentSearchMode, loadVideos,
    handleDiscoverTheme, handleDiscoverForObjective, handleCustomDiscovery,
    handleVideoView, videosByTheme
  } = useVideoData(userId, selectedTheme, searchQuery, selectedObjectiveForVideos, themes, filters);

  // ================================
  // HOOK SYNTHÈSE ACADEMY
  // ================================
  const {
    syntheses,
    loading: synthesisLoading,
    error: synthesisError,
    generateSynthesis,
    saveSynthesis
  } = useAcademySynthesis(userId);

  // ================================
  // FONCTIONS UTILITAIRES STABLES
  // ================================
  const getVideoThemeFromObjective = useCallback((objectiveTheme) => {
    return ALADINN_THEME_MAPPING[objectiveTheme] || 'creativity';
  }, [ALADINN_THEME_MAPPING]);

  const handleVideoClick = useCallback(async (video) => {
    try {
      if (userId) {
        await handleVideoView(video.id);
      }
      
      const url = video.url || video.usableUrl;
      if (url && url !== '#') {
        window.open(url, '_blank');
      }
    } catch (error) {
      console.warn('Erreur ouverture vidéo:', error);
    }
  }, [userId, handleVideoView]);

  const handleThemeSelect = useCallback((themeId) => {
    setSelectedTheme(themeId);
    setSearchQuery('');
    setError(null);
  }, []);

  const parseDuration = useCallback((duration) => {
    if (!duration) return 10;
    const parts = duration.split(':');
    if (parts.length === 2) {
      return parseInt(parts[0]) + (parseInt(parts[1]) / 60);
    }
    return 10;
  }, []);

  // ================================
  // EFFETS DE CHARGEMENT (OPTIMISÉS)
  // ================================
  
  // Effet pour mapper le thème de l'objectif - UNE SEULE FOIS
  useEffect(() => {
    if (objectiveTheme && objectiveId && objectiveTitle) {
      const relevantTheme = getVideoThemeFromObjective(objectiveTheme);
      setSelectedTheme(relevantTheme);
    }
  }, [objectiveTheme, objectiveId, objectiveTitle, getVideoThemeFromObjective]);

  // Effet pour gérer les erreurs du hook
  useEffect(() => {
    if (hookError) {
      setError(hookError);
    }
  }, [hookError]);

  // ================================
  // COMPOSANTS INTERFACE
  // ================================

  const Header = () => {
    const getModeDescription = () => {
      switch (currentSearchMode) {
        case 'objective':
          return 'Sources analysées par IA pour votre objectif';
        case 'search':
        case 'decouverte':
          return `Recherche${currentSearchMode === 'decouverte' ? ' sémantique' : ' textuelle'} dans la base`;
        case 'theme':
        default:
          return `Sources du thème ${themes.find(t => t.id === selectedTheme)?.name}`;
      }
    };

    return (
      <div className="mb-6 md:mb-8">
        <div className="flex flex-col gap-2 mb-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1 flex flex-wrap items-center gap-2">
                <Video className="h-5 w-5 md:h-7 md:w-7 text-purple-600" />
                Academy
                {currentSearchMode === 'objective' && (
                  <span className="text-xs md:text-sm bg-purple-100 text-purple-700 px-2 md:px-3 py-1 rounded-full">
                    Mode Objectif
                  </span>
                )}
                {currentSearchMode === 'decouverte' && (
                  <span className="text-xs md:text-sm bg-green-100 text-green-700 px-2 md:px-3 py-1 rounded-full">
                    Mode Découverte
                  </span>
                )}
              </h1>
              
              <p className="text-sm md:text-base text-gray-600 leading-relaxed hidden md:block">
                {getModeDescription()}
              </p>
            </div>
            
            <div className="hidden md:flex items-center gap-3 ml-4">
              <button
                onClick={() => setShowAdmin(true)}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors shadow-sm text-sm font-medium"
              >
                <Settings className="h-4 w-4" />
                Gérer
              </button>
            </div>
          </div>
        </div>

        {/* Message de découverte */}
        {discoveryMessage && (
          <div className="mb-4 md:mb-6 p-3 md:p-4 rounded-lg border bg-blue-50 border-blue-200">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 md:h-5 md:w-5 text-blue-600" />
              <p className="font-medium text-sm md:text-base text-blue-700">
                {discoveryMessage}
              </p>
            </div>
          </div>
        )}

        {/* Section Objectif */}
        <ObjectiveSection />
      </div>
    );
  };

  const ObjectiveSection = () => {
    if (!selectedObjectiveForVideos) {
      return (
        <div className="bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-4 md:p-6 mb-4 md:mb-6 shadow-sm">
          <div className="text-center py-6 md:py-8">
            <div className="text-purple-600 mb-4">
              <Target className="h-10 w-10 md:h-12 md:w-12 mx-auto mb-3" />
            </div>
            <h4 className="text-base md:text-lg font-semibold text-purple-900 mb-2">Apprentissage ciblé </h4>
            <p className="text-sm md:text-base text-purple-700 mb-4">
              Explorez par thème ou découvrez des vidéos personnalisées
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => handleDiscoverTheme(selectedTheme)}
                disabled={isDiscovering}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm"
              >
                {isDiscovering ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Découvrir
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-4 md:p-6 mb-4 md:mb-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 md:p-3 bg-purple-100 rounded-lg">
            <Target className="h-5 w-5 md:h-6 md:w-6 text-purple-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg md:text-xl font-semibold text-purple-900">Votre objectif</h3>
            <p className="text-sm md:text-base text-purple-700">
              {isDiscovering
                ? 'Découverte en cours...'
                : semanticAnalysisInfo && semanticAnalysisInfo.relevantFound > 0
                  ? `${semanticAnalysisInfo.relevantFound} vidéos disponibles`
                  : 'Objectif sélectionné'
              }
            </p>
          </div>
        </div>

        <div className="bg-white/70 border border-purple-300 rounded-lg p-4 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <div className="w-2 h-2 bg-purple-600 rounded-full mt-2 flex-shrink-0"></div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-purple-900 text-base md:text-lg break-words">
                  {selectedObjectiveForVideos.title}
                </div>
                {selectedObjectiveForVideos.theme && (
                  <div className="text-xs md:text-sm text-purple-700 mt-1">
                    📂 Thème : {selectedObjectiveForVideos.theme}
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <button
                onClick={() => handleDiscoverForObjective(selectedObjectiveForVideos)}
                disabled={isDiscovering}
                className="flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 text-sm"
              >
                {isDiscovering ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Découverte...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    Actualiser
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

      </div>
    );
  };

  const ControlsBar = () => (
    <div className="bg-gray-50 border-b border-gray-200 mb-6">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            {/* Sélecteur de thème */}
            <select
              value={selectedTheme}
              onChange={(e) => handleThemeSelect(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              disabled={selectedObjectiveForVideos}
            >
              {themes.map(theme => (
                <option key={theme.id} value={theme.id}>
                  {theme.icon} {theme.name}
                </option>
              ))}
            </select>

            {/* Recherche */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher des vidéos..."
                className="pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm w-64"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Bouton recherche IA */}
            {searchQuery && (
              <button
                onClick={() => handleCustomDiscovery(searchQuery)}
                disabled={isDiscovering}
                className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2 text-sm"
              >
                {isDiscovering ? <Loader2 className="h-4 w-4 animate-spin" /> : <Brain className="h-4 w-4" />}
                IA
              </button>
            )}

            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-100"
            >
              <Filter className="w-4 h-4" />
              Filtres
              {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={loadVideos}
              disabled={loading}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </button>

            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const VideoGrid = () => {
    if (loading) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({length: 6}).map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm animate-pulse">
              <div className="h-48 bg-gray-200"></div>
              <div className="p-4 space-y-3">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                <div className="h-8 bg-gray-200 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-12">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Erreur de chargement</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={loadVideos}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
          >
            Réessayer
          </button>
        </div>
      );
    }

    // NOUVEAU: Interface spéciale pour objectif vide
    if (filteredVideos.length === 0 && selectedObjectiveForVideos) {
      return (
        <div className="text-center py-12">
          <div className="max-w-md mx-auto">
            {isDiscovering ? (
              // Pendant la découverte automatique
              <>
                <Loader2 className="w-16 h-16 text-blue-600 mx-auto mb-4 animate-spin" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Recherche en cours...
                </h3>
                <p className="text-gray-600 mb-4">
                  Nous recherchons des vidéos pertinentes pour votre objectif
                </p>
                <div className="text-sm text-blue-600 font-medium">
                  "{selectedObjectiveForVideos.title}"
                </div>
              </>
            ) : (
              // Aucune vidéo trouvée même après découverte
              <>
                <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Objectif spécialisé
                </h3>
                <p className="text-gray-600 mb-4">
                  Votre objectif <strong>"{selectedObjectiveForVideos.title}"</strong> nécessite des ressources très spécifiques.
                </p>
                <p className="text-gray-500 mb-6 text-sm">
                  Nous n'avons pas encore trouvé de vidéos parfaitement adaptées à cet objectif.
                </p>
                <div className="space-y-3">
                  <button
                    onClick={() => handleDiscoverForObjective(selectedObjectiveForVideos)}
                    disabled={isDiscovering}
                    className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
                  >
                    {isDiscovering ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
                    Rechercher à nouveau
                  </button>
                  
                  <p className="text-xs text-gray-500">
                    Essayez de reformuler votre objectif ou explorez les thèmes généraux
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      );
    }

    // Interface normale si pas d'objectif spécifique ou si recherche générale
    if (filteredVideos.length === 0) {
      return (
        <div className="text-center py-12">
          <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {searchQuery 
              ? `Aucune vidéo pour "${searchQuery}"`
              : `Aucune vidéo pour ${themes.find(t => t.id === selectedTheme)?.name}`
            }
          </h3>
          <p className="text-gray-600 mb-6">
            Lancez une découverte pour enrichir la base de données
          </p>
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => handleDiscoverTheme(selectedTheme)}
              disabled={isDiscovering}
              className="bg-emerald-600 text-white px-6 py-3 rounded-lg hover:bg-emerald-700 flex items-center gap-2 disabled:opacity-50"
            >
              {isDiscovering ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
              Découvrir
            </button>
          </div>
        </div>
      );
    }

    // Affichage normal des vidéos
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredVideos.map((video, index) => (
          <VideoCard key={video.id || index} video={video} />
        ))}
      </div>
    );
  };


  const VideoCard = ({ video }) => {
    const [imageError, setImageError] = useState(false);
    
    const handleImageError = () => {
      setImageError(true);
    };
    
    const getImageSrc = () => {
      if (imageError || !video.thumbnail) {
        const videoId = video.id ? video.id.slice(-3) : '100';
        return `https://picsum.photos/400/240?random=${videoId}`;
      }
      return video.thumbnail;
    };

    const isRecentVideo = () => {
      const addedDate = new Date(video.addedAt || video.curatedAt || 0);
      const now = new Date();
      const diffHours = (now - addedDate) / (1000 * 60 * 60);
      return diffHours < 48;
    };

    const currentTheme = themes.find(t => t.id === video.theme);

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
        {/* Image */}
        <div className="relative h-48 overflow-hidden">
          <img 
            src={getImageSrc()}
            alt={video.title}
            className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
            loading="lazy"
            onError={handleImageError}
          />
          
          {/* Badge thème */}
          <div className="absolute top-3 left-3">
            <span className={`px-2 py-1 rounded-full text-xs font-medium text-white ${
              currentTheme?.color || 'bg-gray-500'
            }`}>
              {currentTheme?.icon} {currentTheme?.name}
            </span>
          </div>

          {/* Badge récent */}
          {isRecentVideo() && (
            <div className="absolute top-3 right-3">
              <span className="bg-red-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 animate-pulse">
                <Clock className="h-3 w-3" />
                Nouveau
              </span>
            </div>
          )}

          {/* Badge score sémantique */}
          {video.semanticScore && video.semanticScore > 0 && (
            <div className="absolute bottom-3 left-3">
              <span className="bg-blue-600 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                <Brain className="h-3 w-3" />
                Score : {video.semanticScore.toFixed(1)}
              </span>
            </div>
          )}

          {/* Durée */}
          <div className="absolute bottom-3 right-3">
            <span className="bg-black/80 text-white px-2 py-1 rounded text-xs">
              {video.duration || '10:00'}
            </span>
          </div>
        </div>
        
        <div className="p-4">
          <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 leading-tight hover:text-blue-600 transition-colors">
            {video.title}
          </h3>
          
          {/* Description */}
          {(video.description || video.summary) && (
            <p className="text-gray-600 text-sm mb-4 leading-relaxed line-clamp-3">
              {video.summary || video.description}
            </p>
          )}

          <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
            <div className="flex items-center gap-2">
              <span className="font-medium">{video.channelTitle || video.provider || 'Chaîne'}</span>
              <span>•</span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {video.duration || '10:00'}
              </span>
            </div>
            {video.viewCount > 0 && (
              <div className="flex items-center gap-1 text-blue-600">
                <Eye className="w-3 h-3" />
                <span>{video.viewCount}</span>
              </div>
            )}
          </div>
          
          {/* Barre de qualité */}
          {(video.qualityScore || video.qualityRating || video.popularityScore || video.semanticScore) && (
            <div className="mb-3">
              <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
                <span>Qualité</span>
                <span>
                  {video.semanticScore 
                    ? `Score : ${video.semanticScore.toFixed(1)}/10`
                    : `${(video.qualityScore || video.qualityRating || video.popularityScore || 5).toFixed(1)}/10`
                  }
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1">
                <div 
                  className={`h-1 rounded-full transition-all duration-300 ${
                    video.semanticScore ? 'bg-blue-500' : 'bg-green-500'
                  }`}
                  style={{ 
                    width: `${((video.semanticScore || video.qualityScore || video.qualityRating || video.popularityScore || 5) / 10) * 100}%` 
                  }}
                ></div>
              </div>
            </div>
          )}
          
          <div className="flex items-center justify-center">
            <button
              onClick={() => handleVideoClick(video)}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm font-medium transition-colors w-full justify-center"
            >
              <Play className="w-4 h-4" />
              Voir la vidéo
            </button>
          </div>
        </div>
      </div>
    );
  };

  const ThemeSelector = () => {
    const getThemeVideoCount = (themeId) => {
      if (videosByTheme && videosByTheme[themeId]) {
        return videosByTheme[themeId].length;
      }
      return 0;
    };

    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Layers className="h-5 w-5 text-blue-600" />
          Explorer par thème
        </h2>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {themes.map(theme => {
            const isSelected = selectedTheme === theme.id;
            const videoCount = getThemeVideoCount(theme.id);
            
            return (
              <button
                key={theme.id}
                onClick={() => handleThemeSelect(theme.id)}
                disabled={isDiscovering}
                className={`flex flex-col items-center gap-2 p-4 rounded-xl border transition-all hover:scale-105 disabled:opacity-50 ${
                  isSelected ? `${theme.borderColor} ${theme.bgLight} border-2` : 'border-gray-200 hover:border-blue-300 bg-white'
                }`}
              >
                <span className="text-2xl">{theme.icon}</span>
                <span className={`text-sm font-medium ${isSelected ? theme.textColor : 'text-gray-700'}`}>
                  {theme.name}
                </span>
                <span className="text-xs text-gray-500">
                  {videoCount} vidéo{videoCount > 1 ? 's' : ''}
                </span>
                {isDiscovering && selectedTheme === theme.id && (
                  <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // ================================
  // RENDU PRINCIPAL
  // ================================
  return (
    <div className="max-w-6xl mx-auto w-full p-6">
      <Header />
      
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <p className="text-red-800">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-red-500">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      
      {/* Synthèse intelligente pour les objectifs - MAINTENANT AVANT LA GRILLE */}
      {selectedObjectiveForVideos && filteredVideos.length > 0 && (
        <div className="mb-8">
          <AcademySynthesisCard
            videos={filteredVideos}
            objective={selectedObjectiveForVideos}
            userId={userId}
            onSynthesisGenerated={(synthesis) => {
              console.log('Synthèse générée:', synthesis);
            }}
          />
        </div>
      )}

      <div className="mb-8">
        <VideoGrid />
      </div>

      {!selectedObjectiveForVideos && <ThemeSelector />}

      {/* Panneau d'administration */}
      {showAdmin && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-6xl w-full max-h-[90vh] m-4 overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-xl font-semibold">Gestion des Vidéos</h2>
              <button
                onClick={() => {
                  setShowAdmin(false);
                  loadVideos();
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
              <VideoAdminPanel userId={userId} />
            </div>
          </div>
        </div>
      )}

      {/* Styles CSS */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .line-clamp-2 {
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
          
          .line-clamp-3 {
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }

          .hover\\:scale-105:hover {
            transform: scale(1.05);
          }

          .hover\\:-translate-y-1:hover {
            transform: translateY(-0.25rem);
          }

          .transition-all {
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .animate-pulse {
            animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          }

          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: .5; }
          }

          button:focus {
            outline: 2px solid rgb(59 130 246);
            outline-offset: 2px;
          }
        `
      }} />
    </div>
  );
};

export default AladinnAcademy;
